<script lang="ts">
  import { onDestroy, untrack } from 'svelte'
  import type { Token } from 'marked'
  import { servers } from '$lib/store'
  import { VList } from 'virtua/svelte'
  import { getPageCount, getPageCountByBook } from '$lib/apis/book/api'
  import { bookProgress, listBooks, readPage, readPageByBook, updateProgress, markCompleted } from '$lib/apis/read/api'
  import { marked } from 'marked'
  import { goto } from "@mateothegreat/svelte5-router";
  import { fetchTtsMp3, shouldSkipTtsToken, tokenTextForTts } from '$lib/tts'

  import { Spinner } from "$lib/components/ui/spinner/index.js";
  import { Progress } from "$lib/components/ui/progress/index.js";
  import { Switch } from "$lib/components/ui/switch/index.js";
  import { Label } from "$lib/components/ui/label/index.js";
  import { Button, buttonVariants } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Slider } from "$lib/components/ui/slider/index.js";
  import * as Drawer from "$lib/components/ui/drawer/index.js";
  import * as Popover from "$lib/components/ui/popover/index.js";
  import { progressSeries } from '$lib/apis/series/api'
  import ChevronLeft from '@lucide/svelte/icons/chevron-left'
  import ChevronRight from '@lucide/svelte/icons/chevron-right'
  import Play from '@lucide/svelte/icons/play'
  import Pause from '@lucide/svelte/icons/pause'
  import SlidersHorizontal from '@lucide/svelte/icons/sliders-horizontal'
  import X from '@lucide/svelte/icons/x'
  import ArrowLeft from '@lucide/svelte/icons/arrow-left'
  import Check from '@lucide/svelte/icons/check'
  import { READER_BODY_FONT_DEFAULT, isReaderBodyFont } from '$lib/reader-fonts'
  import { readerOpts, readerHeaderVisibleStore } from '$lib/store'
  import { mode } from 'mode-watcher'
  import PLACEHOLDER_MACCHIATO from '$lib/assets/icons/book/volchap_placeholder.svg'
  import PLACEHOLDER_LATTE from '$lib/assets/icons/book/volchap_placeholder_latte.svg'
  import { bookVariantPrefKey } from '$lib/utils'
  import HScrollWithThumb from '$lib/components/nuread/HScrollWithThumb.svelte'
  import MangaPageImage from '$lib/components/nuread/MangaPageImage.svelte'
  import NovelPageContent from '$lib/components/nuread/NovelPageContent.svelte'
  import {
    clearNovelTranslationCache,
    getCachedNovelTranslation,
    setNovelTranslationBackend,
  } from '$lib/novel-translate-cache'

  const { route } = $props()
  const serverid = $derived(route.result.path.params.mediaid)
  const libraryid = $derived(route.result.path.params.libraryid)
  const seriesid = $derived(route.result.path.params.seriesid)
  const type = $derived(route.result.path.params.type)
  const num = $derived(route.result.path.params.num)
  const subNum = $derived(route.result.path.params.subNum)
  const server = $derived($servers[serverid])

  let list: VList<any>
  let tocList: VList<any> | undefined = $state()
  let BASE_URL = $derived($servers[serverid]?.urls?.[0])
  let BASE_URL_SUFFIX = ''

  const coverSrc = $derived(`${BASE_URL}/api/assets/cover/${seriesid}`)
  /** TOC popover list height (matches former max-h-72). */
  const tocListHeightPx = 288
  /** Approximate TOC row height for Virtua sizing. */
  const tocItemSize = 64

  const placeholder = './cover_placeholder.svg'
  let pageCountRes: {
    pageCount?: number
    kind?: string
    title?: string
    translationEnabled?: boolean
    translationBackend?: string
    enhanceEnabled?: boolean
  } = $state({})
  let maxHeight = $state(0)
  let maxWidth = $state(0)
  let offsetHeight = $state(0)
  /** Markdown lexer tokens per page; TTS reads from here + `readPage` while `<audio>` lives above VList (not in list snippets). */
  let content: {[page: number]: any} = $state({})
  let isloaded = $state(false)
  /** VList index to restore after progress fetch; null = pending. */
  let savedPageIndex = $state<number | null>(null)
  /** Within-page scroll fraction 0–1 to restore with savedPageIndex. */
  let savedPageOffset = $state(0)
  let isResuming = $state(false)
  let resumeTarget = $state<number | null>(null)
  let resumeOffset = $state(0)
  let readerSession = $state(0)
  let lastRouteKey = $state('')
  let currentPage = $state(0)
  let localReaderOpts = $state( (() => {
    const stored = localStorage.getItem("readerOpts")
    const opts = stored ? JSON.parse(stored) : {
      fontSize: '20',
      justify: true,
      bodyFont: READER_BODY_FONT_DEFAULT,
      margin: { novel: 5, comics: 0 },
      verticalSpacing: '10',
    }
    if (opts.brightness == null) opts.brightness = 100
    if (opts.contrast == null) opts.contrast = 100
    if (opts.comicsTranslate == null) opts.comicsTranslate = false
    if (opts.novelTranslate == null) opts.novelTranslate = false
    if (opts.comicsEnhance == null) opts.comicsEnhance = true
    // Drop legacy persisted toggle; retranslate is a one-shot button now.
    delete opts.forceRetranslate
    // Margin is % of width (0–50), kept separate per kind.
    if (opts.margin == null || typeof opts.margin !== 'object') {
      const legacy = Math.min(50, Math.max(0, Number(opts.margin) || 0))
      opts.margin = { novel: legacy || 5, comics: legacy }
    } else {
      const novel = Number(opts.margin.novel)
      const comics = Number(opts.margin.comics)
      opts.margin.novel = Math.min(50, Math.max(0, Number.isFinite(novel) ? novel : 5))
      opts.margin.comics = Math.min(50, Math.max(0, Number.isFinite(comics) ? comics : 0))
    }
    if (!isReaderBodyFont(opts.bodyFont)) {
      opts.bodyFont = READER_BODY_FONT_DEFAULT
    }
    return opts
  })())
  /** One-shot force-retranslate request for a manga page (API 1-based page num). */
  let retranslateRequest = $state<{ pageNum: number; token: number } | null>(null)
  let retranslateTokenSeq = 0
  let booksRes = $state([])
  let progRes = $state<any>({})
  let tocOpen = $state(false)
  let pageJumpOpen = $state(false)
  let pageJumpInput = $state('')
  let pageCountCache = $state<Record<string, number>>({})

  function findCurrentBook(books: any[]) {
    const targetSub = subNum ?? '0'
    const matches = books.filter((e: any) => {
      if (e.type !== type) return false
      const n = type === 'volume' ? e.volume : e.chapter
      const sn = type === 'volume' ? (e.subVolume ?? 0) : (e.subChapter ?? 0)
      return String(n) === String(num) && String(sn) === String(targetSub)
    })
    if (matches.length === 0) return undefined
    if (matches.length === 1) return matches[0]
    try {
      const pref = localStorage.getItem(
        bookVariantPrefKey(seriesid, type, num, targetSub)
      )
      return matches.find((m: any) => m.id === pref) ?? matches[0]
    } catch {
      return matches[0]
    }
  }

  const bookid = $derived(findCurrentBook(booksRes)?.id)
  /** Chapter path without bookid — used to detect listBooks resolving id without a real navigation. */
  const chapterRouteKey = $derived(
    `${serverid}:${seriesid}:${type}:${num}:${subNum ?? ''}`
  )
  const routeKey = $derived(`${chapterRouteKey}:${bookid ?? ''}`)

  let lastScrollOffset = $state(0)
  let bottomBarVisible = $state(true)
  const scrollThreshold = 30

  let kind = $derived(pageCountRes?.kind)
  let kindUpper = $derived(String(kind ?? '').toUpperCase())
  let isMangaLike = $derived(['WEBTOON', 'MANGA'].includes(kindUpper))
  let isNovelKind = $derived(['NOVEL', 'WEBNOVEL'].includes(kindUpper))
  let mangaTranslationAvailable = $derived(pageCountRes?.translationEnabled === true)
  let novelTranslationAvailable = $derived(pageCountRes?.translationEnabled === true)
  let mangaEnhanceAvailable = $derived(pageCountRes?.enhanceEnabled === true)
  let comicsTranslateActive = $derived(
    isMangaLike && mangaTranslationAvailable && localReaderOpts.comicsTranslate,
  )
  let novelTranslateActive = $derived(
    isNovelKind && novelTranslationAvailable && localReaderOpts.novelTranslate,
  )
  let comicsEnhanceActive = $derived(
    isMangaLike &&
      mangaEnhanceAvailable &&
      localReaderOpts.comicsEnhance &&
      !comicsTranslateActive,
  )

  const readerControl =
    'rounded-md border border-latte-surface1/60 bg-latte-base/70 text-latte-text transition-colors hover:border-latte-mauve/50 hover:text-latte-mauve dark:border-macchiato-surface1/60 dark:bg-macchiato-base/70 dark:text-macchiato-text dark:hover:border-macchiato-mauve/50 dark:hover:text-macchiato-mauve'
  const tocCoverClass =
    'size-10 shrink-0 overflow-hidden rounded-sm bg-latte-crust dark:bg-macchiato-base sm:size-11'
  const sectionLabel =
    'text-[10px] font-medium uppercase tracking-wider text-latte-surface1 dark:text-macchiato-surface1'
  const fontChoices = [
    { id: 'crimson-text', label: 'Crimson' },
    { id: 'nunito', label: 'Nunito' },
    { id: 'fira-code', label: 'Fira Code' },
    { id: 'noto-sans', label: 'Noto Sans' },
  ] as const

  let marginPct = $derived(
    Math.min(
      50,
      Math.max(
        0,
        Number(localReaderOpts.margin?.[isMangaLike ? 'comics' : 'novel']) || 0,
      ),
    ),
  )

  function dedupeVolumesSorted(books: any[]) {
    return books.filter((e, i, arr) => {
      if (i === 0) return true
      const prev = arr[i - 1]
      return !(
        e.volume === prev.volume &&
        (e.subVolume ?? null) === (prev.subVolume ?? null)
      )
    })
  }

  function dedupeChaptersSorted(books: any[]) {
    return books.filter((e, i, arr) => {
      if (i === 0) return true
      const prev = arr[i - 1]
      return !(
        e.chapter === prev.chapter &&
        (e.subChapter ?? null) === (prev.subChapter ?? null)
      )
    })
  }

  function attachReadingProgress(books: any[], progList: any[]) {
    return books.map((b: any) => {
      const prog = progList.find((p: any) => p.bookID == b.id)
      return {
        ...b,
        progression: prog?.Current,
        isCompleted: prog?.IsCompleted,
      }
    })
  }

  const progress = $derived(progRes.Progress ?? [])
  const navEntries = $derived.by(() => {
    const raw = booksRes.filter((e: any) => e.type === type)
    const sorted = [...raw].sort((a: any, b: any) => {
      if (type === 'volume') {
        return a.volume - b.volume || (a.subVolume ?? 0) - (b.subVolume ?? 0)
      }
      return a.chapter - b.chapter || (a.subChapter ?? 0) - (b.subChapter ?? 0)
    })
    const deduped =
      type === 'volume' ? dedupeVolumesSorted(sorted) : dedupeChaptersSorted(sorted)
    return attachReadingProgress(deduped, progress)
  })

  function entryNum(book: any) {
    return type === 'volume' ? book.volume : book.chapter
  }

  function entrySubNum(book: any) {
    const sn = type === 'volume' ? book.subVolume : book.subChapter
    return sn == null || sn === '' ? '0' : String(sn)
  }

  function entryLabel(book: any) {
    const n = entryNum(book)
    const sn = entrySubNum(book)
    return `${type === 'volume' ? 'Vol.' : 'Chp.'} ${n}${sn !== '0' ? '.' + sn : ''}`
  }

  function readerHref(book: any) {
    const n = entryNum(book)
    const sn = entrySubNum(book)
    return `/media/${serverid}/library/${libraryid}/series/${seriesid}/${type}/${n}${sn !== '0' ? '.' + sn : ''}`
  }

  function isCurrentEntry(book: any) {
    const currentSub = subNum ?? '0'
    return String(entryNum(book)) === String(num) && entrySubNum(book) === String(currentSub)
  }

  function goToReader(href: string) {
    tocOpen = false
    goto(href)
  }

  function goToSeries() {
    tocOpen = false
    goto(`/media/${serverid}/library/${libraryid}/series/${seriesid}`)
  }

  const nextNavEntry = $derived.by(() => {
    const i = navEntries.findIndex(isCurrentEntry)
    return i >= 0 ? navEntries[i + 1] ?? null : null
  })

  const volChapPlaceholder = $derived(
    mode.current === 'dark' ? PLACEHOLDER_MACCHIATO : PLACEHOLDER_LATTE
  )

  function bookCoverSrc(bookId: string) {
    const base = BASE_URL
    if (!base) return volChapPlaceholder
    let src = `${base}/api/v1/read/${bookId}/cover`
    if (base.indexOf('http:') != -1) {
      src = `https://proxy.nuread.direct.habysmol.com/api/v1/read/${bookId}/cover?upstream_addr=${base.replace('http://', '')}`
    }
    return src
  }

  function entryPageTotal(book: any) {
    if (isCurrentEntry(book) && pageCount > 0) return pageCount
    return pageCountCache[book.id]
  }

  /** 1-based page position; live `currentPage` for the open entry. */
  function entryProgressCurrent(book: any) {
    if (isCurrentEntry(book)) return currentPage + 1
    return book.progression
  }

  const pageCountInflight = new Set<string>()

  function fetchEntryPageCount(book: any) {
    if (!book?.id || book.isCompleted) return
    if (isCurrentEntry(book)) return
    if (pageCountCache[book.id] != null || pageCountInflight.has(book.id)) return
    const srv = $servers[serverid]
    if (!srv || !seriesid) return
    pageCountInflight.add(book.id)
    const n = String(entryNum(book))
    const sn = entrySubNum(book)
    getPageCount(srv, seriesid, type, n, sn)
      .then((r) => {
        const count = r?.pageCount
        if (count == null) return
        pageCountCache = { ...pageCountCache, [book.id]: count }
      })
      .finally(() => {
        pageCountInflight.delete(book.id)
      })
  }

  /** Fetch page counts only for TOC rows currently in (or near) the virtual viewport. */
  function ensureVisibleTocPageCounts() {
    if (!tocList || navEntries.length === 0) return
    const offset = tocList.getScrollOffset()
    const viewport = tocList.getViewportSize() || tocListHeightPx
    let start = tocList.findItemIndex(offset)
    let end = tocList.findItemIndex(offset + viewport)
    if (start < 0) start = 0
    if (end < 0) end = navEntries.length - 1
    start = Math.max(0, start - 2)
    end = Math.min(navEntries.length - 1, end + 2)
    for (let i = start; i <= end; i++) {
      fetchEntryPageCount(navEntries[i])
    }
  }

  function entryProgressPercent(book: any) {
    if (!isCurrentEntry(book) && book.isCompleted) return 100
    const total = entryPageTotal(book)
    const current = entryProgressCurrent(book)
    if (!total || !current) return 0
    return Math.min(100, (current / total) * 100)
  }

  function entryProgressStatus(book: any) {
    if (!isCurrentEntry(book) && book.isCompleted) return 'Completed'
    const total = entryPageTotal(book)
    const current = entryProgressCurrent(book)
    if (!current) return 'Not started'
    if (total) {
      const pct = Math.round(entryProgressPercent(book))
      return `Page ${current} / ${total} · ${pct}%`
    }
    return `Page ${current}`
  }

  function entryProgressBarClass(book: any, pct: number) {
    const base =
      'h-1 bg-latte-surface1/40 dark:bg-macchiato-surface1/40 [&_[data-slot=progress-indicator]]:transition-all'
    if (!isCurrentEntry(book) && book.isCompleted) {
      return `${base} [&_[data-slot=progress-indicator]]:bg-latte-green dark:[&_[data-slot=progress-indicator]]:bg-macchiato-green`
    }
    if (pct > 0) {
      return `${base} [&_[data-slot=progress-indicator]]:bg-latte-mauve dark:[&_[data-slot=progress-indicator]]:bg-macchiato-mauve`
    }
    return `${base} [&_[data-slot=progress-indicator]]:bg-transparent`
  }

  let novelTtsPage = $state<number | null>(null)
  let novelTtsSpokenThrough = $state(-1)
  let novelTtsBusy = $state(false)
  /** Mirrors actual playback — `audioEl.paused` is not reactive in templates. */
  let novelTtsAudioPlaying = $state(false)
  let audioEl = $state<HTMLAudioElement | null>(null)

  $effect(() => {
    const el = audioEl
    if (!el) return
    const syncFromElement = () => {
      novelTtsAudioPlaying = !el.paused
    }
    el.addEventListener('play', syncFromElement)
    el.addEventListener('pause', syncFromElement)
    syncFromElement()
    return () => {
      el.removeEventListener('play', syncFromElement)
      el.removeEventListener('pause', syncFromElement)
    }
  })
  let ttsAbort: AbortController | null = null
  let ttsObjectUrl: string | null = null

  const TTS_PREFETCH_AHEAD = 2
  const ttsPrefetchCache = new Map<string, string>()
  const ttsPrefetchInflight = new Map<string, AbortController>()

  function ttsSegmentKey(page: number, idx: number) {
    return `${page}:${idx}`
  }

  function abortTtsPrefetches() {
    for (const ac of ttsPrefetchInflight.values()) {
      ac.abort()
    }
    ttsPrefetchInflight.clear()
  }

  function revokeTtsPrefetchCache() {
    for (const url of ttsPrefetchCache.values()) {
      URL.revokeObjectURL(url)
    }
    ttsPrefetchCache.clear()
  }

  async function collectNextSpeakableSegments(
    startPage: number,
    startTokenIdx: number,
    count: number,
  ): Promise<Array<{ page: number; idx: number; text: string }>> {
    const out: Array<{ page: number; idx: number; text: string }> = []
    let page = startPage
    let idx = startTokenIdx
    while (out.length < count && page < pageCount) {
      // page is VList index; read API is 1-based
      const tokens = await displayPageTokens(page + 1)
      while (idx < tokens.length) {
        if (!shouldSkipTtsToken(tokens[idx])) {
          const t = tokenTextForTts(tokens[idx])
          if (t) {
            out.push({ page, idx, text: t })
            if (out.length >= count) return out
          }
        }
        idx++
      }
      page++
      idx = 0
    }
    return out
  }

  function scheduleTtsPrefetch(
    segments: Array<{ page: number; idx: number; text: string }>,
  ) {
    if (novelTtsPage === null) return
    for (const seg of segments) {
      const key = ttsSegmentKey(seg.page, seg.idx)
      if (ttsPrefetchCache.has(key) || ttsPrefetchInflight.has(key)) continue
      const ac = new AbortController()
      ttsPrefetchInflight.set(key, ac)
      fetchTtsMp3(seg.text, ac.signal)
        .then((blob) => {
          ttsPrefetchInflight.delete(key)
          if (novelTtsPage === null) return
          const url = URL.createObjectURL(blob)
          ttsPrefetchCache.set(key, url)
        })
        .catch((e) => {
          ttsPrefetchInflight.delete(key)
          if ((e as Error).name !== 'AbortError') console.error(e)
        })
    }
  }

  function kickTtsPrefetch(fromPage: number, currentTokenIdx: number) {
    void collectNextSpeakableSegments(fromPage, currentTokenIdx + 1, TTS_PREFETCH_AHEAD).then(
      (segments) => {
        if (novelTtsPage === null) return
        scheduleTtsPrefetch(segments)
      },
    )
  }

  $effect(() => {
    if (!server || !seriesid) return
    listBooks(server, seriesid).then((r: any) => (booksRes = r.Books ?? []))
  })
  $effect(() => {
    if (!server || !seriesid) return
    progressSeries(server, seriesid).then((r: any) => (progRes = r ?? {}))
  })

  $effect(() => {
    if (!tocOpen || !tocList || navEntries.length === 0) return
    const idx = navEntries.findIndex((book) => isCurrentEntry(book))
    requestAnimationFrame(() => {
      if (idx >= 0) tocList?.scrollToIndex(idx, { align: 'center' })
      ensureVisibleTocPageCounts()
    })
  })
  $effect(() => {
    const session = readerSession
    const srv = server
    if (!srv) return

    if (!bookid) {
      if (booksRes.length > 0 && savedPageIndex === null) {
        savedPageIndex = 0
      }
      return
    }

    savedPageIndex = null
    savedPageOffset = 0
    bookProgress(srv, seriesid, bookid).then((r: any) => {
      if (session !== readerSession) return
      const current = r?.Progress?.Current
      savedPageIndex = current > 0 ? current - 1 : 0
      const off = Number(r?.Progress?.Offset)
      savedPageOffset = Number.isFinite(off) ? Math.min(1, Math.max(0, off)) : 0
    })
  })

  $effect(() => readerOpts.set(localReaderOpts))

  let pageCount = $derived(pageCountRes?.pageCount ?? 0)
  let title = $derived(pageCountRes?.title)
  /** 1-based read API page numbers for all kinds (manga + novel). */
  let pages = $derived(Array.from({ length: pageCount }, (_, i) => i + 1))
  let listHeight = $derived(maxHeight - offsetHeight)
  /** Comics inset per side (px): half of (width × comics%). */
  const comicsMarginPx = $derived(
    (maxWidth * Math.min(50, Math.max(0, Number(localReaderOpts.margin?.comics) || 0))) /
      100 /
      2,
  )
  /** Display width for a single manga/webtoon page (viewport minus gutters + margin). */
  const mangaSinglePageWidth = $derived(Math.max(0, maxWidth - 2 * comicsMarginPx - 10))
  const mangaPageEstimate = $derived(Math.max(350, Math.round(mangaSinglePageWidth * 1.45)))
  /** Pages whose natural aspect ratio looks like a double-page spread. */
  let mangaPageIsDouble = $state<Record<number, boolean>>({})
  const resumeKeepMounted = $derived(
    isResuming && resumeTarget !== null ? [resumeTarget] : undefined
  )

  function mangaPageDisplayWidth(index: number) {
    return mangaSinglePageWidth * (mangaPageIsDouble[index] ? 2 : 1)
  }

  function onMangaPageLoad(e: Event, index: number) {
    const img = e.currentTarget as HTMLImageElement
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      // Portrait ≈ 0.65–0.8; spreads are roughly twice as wide (≥ ~1.0).
      const isDouble = img.naturalWidth / img.naturalHeight >= 1
      if (mangaPageIsDouble[index] !== isDouble) {
        mangaPageIsDouble = { ...mangaPageIsDouble, [index]: isDouble }
      }
    }
    onPageContentResize(index)
  }

  $effect(() => {
    if (!server || !seriesid) return
    if (bookid) {
      getPageCountByBook(server, bookid).then((r) => {
        pageCountRes = r
      })
      return
    }
    getPageCount(server, seriesid, type, num, subNum ?? '0').then((r) => {
      pageCountRes = r
    })
  })

  $effect(() => {
    if (pageCountRes?.translationBackend) {
      setNovelTranslationBackend(pageCountRes.translationBackend)
    }
  })

  async function renderPage(page: number) {
    let tokens = content[page]
    if (tokens === undefined) {
      let data
      if (bookid) {
        data =
          (await readPageByBook($servers[serverid as string], bookid, page)) ?? ''
      } else {
        data =
          (await readPage(
            $servers[serverid as string],
            seriesid,
            type,
            num,
            subNum ?? '0',
            page,
          )) ?? ''
      }
      tokens = marked.lexer(typeof data === 'string' ? data : String(data ?? ''))
      content[page] = tokens
    }
    return tokens
  }

  /** Prefer cached translation for TTS when novel translate is on. */
  async function displayPageTokens(page: number): Promise<Token[]> {
    const original = Array.from((await renderPage(page)) as Token[])
    if (!novelTranslateActive) return original
    const cacheId = bookid || `${seriesid}:${type}:${num}:${subNum ?? '0'}`
    const cached = getCachedNovelTranslation(cacheId, page)
    if (cached != null) return marked.lexer(cached) as Token[]
    return original
  }

  function revokeTtsObjectUrl() {
    if (ttsObjectUrl) {
      URL.revokeObjectURL(ttsObjectUrl)
      ttsObjectUrl = null
    }
  }

  function stopNovelTts() {
    ttsAbort?.abort()
    ttsAbort = null
    abortTtsPrefetches()
    revokeTtsPrefetchCache()
    revokeTtsObjectUrl()
    if (audioEl) {
      audioEl.onended = null
      audioEl.pause()
      audioEl.src = ''
    }
    novelTtsPage = null
    novelTtsSpokenThrough = -1
    novelTtsBusy = false
    novelTtsAudioPlaying = false
  }

  async function advanceNovelTts(pageIndex: number, startIdx: number) {
    if (novelTtsPage !== pageIndex) return
    // pageIndex is VList index; read API is 1-based
    const tokens = await displayPageTokens(pageIndex + 1)
    let idx = startIdx
    while (idx < tokens.length && shouldSkipTtsToken(tokens[idx])) idx++
    if (idx >= tokens.length) {
      const nextPage = pageIndex + 1
      if (isNovelKind && nextPage < pageCount) {
        novelTtsPage = nextPage
        novelTtsSpokenThrough = -1
        novelTtsBusy = false
        list?.scrollToIndex(nextPage)
        queueMicrotask(() => handleScroll())
        requestAnimationFrame(() => {
          handleScroll()
          requestAnimationFrame(() => handleScroll())
        })
        void advanceNovelTts(nextPage, 0)
      } else {
        stopNovelTts()
      }
      return
    }
    const text = tokenTextForTts(tokens[idx])
    if (!text) {
      void advanceNovelTts(pageIndex, idx + 1)
      return
    }

    const segKey = ttsSegmentKey(pageIndex, idx)
    const cachedUrl = ttsPrefetchCache.get(segKey)
    if (cachedUrl) {
      ttsPrefetchCache.delete(segKey)
      if (novelTtsPage !== pageIndex) {
        URL.revokeObjectURL(cachedUrl)
        return
      }
      revokeTtsObjectUrl()
      ttsObjectUrl = cachedUrl
      if (!audioEl) return
      audioEl.onended = () => {
        novelTtsSpokenThrough = idx
        novelTtsBusy = false
        void advanceNovelTts(pageIndex, idx + 1)
      }
      audioEl.src = cachedUrl
      await audioEl.play().catch((err) => {
        console.error(err)
        stopNovelTts()
      })
      novelTtsAudioPlaying = !audioEl.paused
      kickTtsPrefetch(pageIndex, idx)
      return
    }

    novelTtsBusy = true
    ttsAbort = new AbortController()
    try {
      const blob = await fetchTtsMp3(text, ttsAbort.signal)
      if (novelTtsPage !== pageIndex) return
      revokeTtsObjectUrl()
      ttsObjectUrl = URL.createObjectURL(blob)
      if (!audioEl) return
      audioEl.onended = () => {
        novelTtsSpokenThrough = idx
        novelTtsBusy = false
        void advanceNovelTts(pageIndex, idx + 1)
      }
      audioEl.src = ttsObjectUrl
      await audioEl.play().catch((err) => {
        console.error(err)
        stopNovelTts()
      })
      novelTtsBusy = false
      novelTtsAudioPlaying = !audioEl.paused
      kickTtsPrefetch(pageIndex, idx)
    } catch (e) {
      novelTtsBusy = false
      if ((e as Error).name === 'AbortError') return
      console.error(e)
      stopNovelTts()
    }
  }

  function startNovelTtsFromCurrentPage() {
    if (!isNovelKind || pageCount === 0) return
    stopNovelTts()
    novelTtsPage = currentPage
    novelTtsSpokenThrough = -1
    void advanceNovelTts(currentPage, 0)
  }

  function toggleNovelTtsPlayPause() {
    if (!isNovelKind || pageCount === 0) return
    if (novelTtsBusy) {
      stopNovelTts()
      return
    }
    if (novelTtsPage === null) {
      startNovelTtsFromCurrentPage()
      return
    }
    const a = audioEl
    if (!a) return
    if (a.paused) {
      void a.play().then(() => {
        novelTtsAudioPlaying = true
      }).catch((err) => console.error(err))
    } else {
      a.pause()
      novelTtsAudioPlaying = false
    }
  }

  function novelTtsTokenDimmed(pageIndex: number, ttsIdx: number) {
    return (
      novelTtsPage !== null &&
      novelTtsPage === pageIndex &&
      ttsIdx <= novelTtsSpokenThrough
    )
  }

  function requestRetranslateCurrentPage() {
    if (pageCount === 0) return
    const pageNum = pages[currentPage]
    if (pageNum == null) return
    if (comicsTranslateActive) {
      retranslateTokenSeq++
      retranslateRequest = { pageNum, token: retranslateTokenSeq }
      return
    }
    if (novelTranslateActive) {
      retranslateTokenSeq++
      retranslateRequest = { pageNum, token: retranslateTokenSeq }
    }
  }

  function onRetranslateConsumed(pageNum: number, token: number) {
    if (
      retranslateRequest &&
      retranslateRequest.pageNum === pageNum &&
      retranslateRequest.token === token
    ) {
      retranslateRequest = null
    }
  }

  $effect(() => {
    const key = routeKey
    if (key === lastRouteKey) return

    untrack(() => {
      // listBooks resolving bookid ('' → uuid) must not wipe an already-loaded chapter:
      // that shrinks `pages` while Virtua still holds stale indexes → each_key_duplicate.
      if (
        bookid &&
        lastRouteKey === `${chapterRouteKey}:` &&
        key === `${chapterRouteKey}:${bookid}`
      ) {
        lastRouteKey = key
        return
      }

      flushProgressUpdate()
      lastRouteKey = key
      readerSession++
      isloaded = false
      savedPageIndex = null
      savedPageOffset = 0
      isResuming = false
      resumeTarget = null
      resumeOffset = 0
      clearResumeTimers()
      currentPage = 0
      content = {}
      pageCountRes = {}
      mangaPageIsDouble = {}
      lastScrollOffset = 0
      lastFlushedProgressKey = ''
      retranslateRequest = null
      pullPx = 0
      pullAnimating = false
      pullNavigating = false
      pullTouchActive = false
      pullTouchLocked = 'none'
      clearPullWheelTimer()
      stopNovelTts()
    })
  })

  onDestroy(() => {
    flushProgressUpdate()
    clearResumeTimers()
    clearPullWheelTimer()
    stopNovelTts()
  })

  function onDocumentVisibilityChange() {
    if (document.visibilityState === 'hidden') flushProgressUpdate()
  }

  /** Viewport-bottom probe used by virtua to decide which page is "current". */
  function scrollProbeOffset() {
    if (!list) return 0
    return list.getScrollOffset() - maxHeight + offsetHeight + listHeight
  }

  function visiblePageIndex() {
    if (!list) return -1
    return list.findItemIndex(scrollProbeOffset())
  }

  /** Fraction 0–1 through the given VList item (based on viewport-bottom probe). */
  function pageOffsetFraction(index: number) {
    if (!list || index < 0) return 0
    const size = list.getItemSize(index)
    if (size <= 0) return 0
    const raw = (scrollProbeOffset() - list.getItemOffset(index)) / size
    return Math.min(1, Math.max(0, raw))
  }

  /**
   * Hysteresis around page boundaries so tiny back/forth scrolls near the edge
   * don't flip the displayed page number / progress.
   */
  function stablePageIndex() {
    if (!list) return currentPage
    const raw = visiblePageIndex()
    if (raw < 0) return currentPage
    if (raw === currentPage) return currentPage

    // Large jumps (page jump / resume / fast fling) commit immediately.
    if (Math.abs(raw - currentPage) > 1) return raw

    const hysteresis = Math.max(64, Math.round(listHeight * 0.2))
    const probe = scrollProbeOffset()

    if (raw > currentPage) {
      // Advancing: require probe past the next page's start by a safe gap.
      const boundary = list.getItemOffset(currentPage + 1)
      return probe >= boundary + hysteresis ? raw : currentPage
    }

    // Going back: require probe before the current page's start by a safe gap.
    const boundary = list.getItemOffset(currentPage)
    return probe < boundary - hysteresis ? raw : currentPage
  }

  function applyResumeScrollOffset() {
    if (resumeTarget === null || !list || !isResuming) return
    const offset = Math.min(1, Math.max(0, resumeOffset))
    if (offset <= 0) return
    const itemOffset = list.getItemOffset(resumeTarget)
    const itemSize = list.getItemSize(resumeTarget)
    if (itemSize <= 0) return
    lastResumeScrollAt = Date.now()
    list.scrollTo(itemOffset + offset * itemSize)
  }

  function scrollToResumeTarget(session: number) {
    if (session !== readerSession || resumeTarget === null || !list || !isResuming) return
    lastResumeScrollAt = Date.now()
    list.scrollToIndex(resumeTarget, { align: 'start' })
    if (resumeOffset > 0) {
      // Apply after virtua lays out the target; settleResume re-applies as heights change.
      requestAnimationFrame(() => {
        if (session !== readerSession || !isResuming) return
        applyResumeScrollOffset()
      })
    }
  }

  let resumeSettleGeneration = 0
  let resumeSettleTimer: ReturnType<typeof setTimeout> | undefined
  let resumeSettleTimers: ReturnType<typeof setTimeout>[] = []
  let lastResumeScrollAt = 0

  function clearResumeTimers() {
    resumeSettleGeneration++
    if (resumeSettleTimer) clearTimeout(resumeSettleTimer)
    resumeSettleTimer = undefined
    for (const t of resumeSettleTimers) clearTimeout(t)
    resumeSettleTimers = []
  }

  function finishResume(session: number) {
    if (session !== readerSession || !isResuming) return
    clearResumeTimers()
    const target = resumeTarget
    isResuming = false
    resumeTarget = null
    resumeOffset = 0
    isloaded = true
    if (target !== null) currentPage = target
    handleScroll()
  }

  function cancelResume() {
    if (!isResuming) return
    clearResumeTimers()
    isResuming = false
    resumeTarget = null
    resumeOffset = 0
    isloaded = true
  }

  function onPageContentResize(index: number) {
    if (!isResuming || resumeTarget === null || index > resumeTarget) return
    scheduleResumeSettle(readerSession)
  }

  function observePageResize(node: HTMLElement, index: number) {
    const ro = new ResizeObserver(() => onPageContentResize(index))
    ro.observe(node)
    return {
      destroy() {
        ro.disconnect()
      },
    }
  }

  function scheduleResumeSettle(session: number) {
    if (!isResuming) return
    if (resumeSettleTimer) clearTimeout(resumeSettleTimer)
    resumeSettleTimer = setTimeout(() => {
      resumeSettleTimer = undefined
      settleResume(session, 0, resumeSettleGeneration)
    }, 80)
  }

  function settleResume(session: number, pass: number, gen: number) {
    if (
      gen !== resumeSettleGeneration ||
      session !== readerSession ||
      resumeTarget === null ||
      !list ||
      !isResuming
    ) {
      return
    }

    const visible = visiblePageIndex()
    const maxPasses = isMangaLike ? 15 : 8
    const intervalMs = isMangaLike ? 300 : 200

    if (visible !== resumeTarget) {
      scrollToResumeTarget(session)
    } else {
      // Heights may still be settling; keep within-page offset applied.
      applyResumeScrollOffset()
      if (pass >= 2) {
        finishResume(session)
        return
      }
    }

    if (pass >= maxPasses) {
      finishResume(session)
      return
    }

    const timer = setTimeout(() => settleResume(session, pass + 1, gen), intervalMs)
    resumeSettleTimers.push(timer)
  }

  function resumeScroll(target: number, session: number, offset = 0, attempt = 0) {
    if (session !== readerSession) return
    if (!list) {
      if (attempt < 60) setTimeout(() => resumeScroll(target, session, offset, attempt + 1), 50)
      return
    }

    clearResumeTimers()
    isResuming = true
    resumeTarget = target
    resumeOffset = Math.min(1, Math.max(0, offset))
    const gen = resumeSettleGeneration
    scrollToResumeTarget(session)
    settleResume(session, 0, gen)
  }

  const PROGRESS_DEBOUNCE_MS = 400
  const OFFSET_SAVE_DELTA = 0.02

  type PendingProgress = {
    server: any
    seriesId: string
    bookId: string
    page: number
    offset: number
    pageCount: number
  }

  let progressTimer: ReturnType<typeof setTimeout> | undefined
  let pendingProgress: PendingProgress | null = null
  let lastFlushedProgressKey = ''
  let lastScheduledOffset = 0

  function clearProgressTimer() {
    if (progressTimer) clearTimeout(progressTimer)
    progressTimer = undefined
  }

  /** Coalesce rapid page/offset changes; pending snapshot survives route/book changes until flush. */
  function scheduleProgressUpdate(page1Based: number, offset = 0) {
    if (!bookid || !server || page1Based < 1) return
    const clampedOffset = Math.min(1, Math.max(0, offset))
    pendingProgress = {
      server,
      seriesId: seriesid,
      bookId: bookid,
      page: page1Based,
      offset: clampedOffset,
      pageCount,
    }
    lastScheduledOffset = clampedOffset
    clearProgressTimer()
    progressTimer = setTimeout(() => {
      progressTimer = undefined
      flushProgressUpdate()
    }, PROGRESS_DEBOUNCE_MS)
  }

  function flushProgressUpdate() {
    clearProgressTimer()
    const pending = pendingProgress
    if (!pending) return
    pendingProgress = null
    const offsetKey = Math.round(pending.offset * 100)
    const key = `${pending.bookId}:${pending.page}:${offsetKey}`
    if (key === lastFlushedProgressKey) return
    lastFlushedProgressKey = key
    updateProgress(pending.server, pending.seriesId, pending.bookId, pending.page, pending.offset)
    // page is 1-based; same as former `currentPage + 2 >= pageCount`
    if (pending.page + 1 >= pending.pageCount) {
      markCompleted(pending.server, pending.seriesId, pending.bookId)
    }
  }

  function jumpToPage() {
    const parsed = parseInt(pageJumpInput, 10)
    if (!Number.isFinite(parsed) || pageCount === 0 || !list) return

    const target = Math.min(Math.max(1, parsed), pageCount) - 1
    pageJumpOpen = false

    if (isResuming) cancelResume()

    isloaded = true
    list.scrollToIndex(target, { align: 'start' })
    currentPage = target

    if (bookid && server) {
      scheduleProgressUpdate(target + 1, 0)
      flushProgressUpdate()
    }

    handleScroll()
  }

  function handleScroll() {
    if (!list) return
    const scrollOffset = list.getScrollOffset()

    if (
      isResuming &&
      Math.abs(scrollOffset - lastScrollOffset) > scrollThreshold &&
      Date.now() - lastResumeScrollAt > 400
    ) {
      cancelResume()
    }

    if (Math.abs(scrollOffset - lastScrollOffset) > scrollThreshold) {
      bottomBarVisible = scrollOffset <= lastScrollOffset
      lastScrollOffset = scrollOffset
      readerHeaderVisibleStore.set(bottomBarVisible)
    }
    const current = stablePageIndex()
    if (!isloaded || isResuming || !bookid || !server || current < 0) return

    const offset = pageOffsetFraction(current)
    const pageChanged = currentPage !== current
    const offsetChanged = Math.abs(offset - lastScheduledOffset) >= OFFSET_SAVE_DELTA
    if (pageChanged || offsetChanged) {
      currentPage = current
      scheduleProgressUpdate(currentPage + 1, offset)
    }
  }

  /** Pull-past-end → next chapter/volume (mark-complete path unchanged). */
  const PULL_THRESHOLD_MIN_PX = 80
  const PULL_DAMPING = 0.35
  const PULL_WHEEL_END_MS = 120
  const PULL_RING_R = 10
  const PULL_RING_C = 2 * Math.PI * PULL_RING_R
  const pullThresholdPx = $derived(
    Math.max(PULL_THRESHOLD_MIN_PX, Math.round(maxHeight * 0.1)),
  )
  const pullMaxPx = $derived(Math.round(pullThresholdPx * 1.6))

  let pullPx = $state(0)
  let pullAnimating = $state(false)
  let pullNavigating = $state(false)
  let pullTouchStartY = 0
  let pullTouchStartX = 0
  let pullTouchActive = false
  let pullTouchLocked: 'none' | 'vertical' | 'horizontal' = 'none'
  let pullWheelEndTimer: ReturnType<typeof setTimeout> | undefined

  /**
   * True when the list cannot scroll further down (pull-to-next may start).
   *
   * Do not require `currentPage === last`: page index uses a viewport-bottom probe
   * plus hysteresis, so a short final page often never becomes "current" even when
   * scroll is already pinned at the end. Scroll-end + last page in/near the
   * viewport is enough; mid-list flings still fail the scroll-end check.
   */
  function atListEnd() {
    if (!list || pageCount === 0 || isResuming || pullNavigating) return false

    const viewport = list.getViewportSize()
    const maxScroll = list.getScrollSize() - viewport
    const atScrollEnd = maxScroll <= 0 || list.getScrollOffset() >= maxScroll - 2
    if (!atScrollEnd) return false

    const last = pageCount - 1
    if (currentPage >= last) return true

    const visible = visiblePageIndex()
    if (visible >= last) return true

    // Last page has entered the viewport even if probe/hysteresis lag behind.
    const lastTop = list.getItemOffset(last)
    const scrollBottom = list.getScrollOffset() + viewport
    return scrollBottom >= lastTop - 2
  }

  function clearPullWheelTimer() {
    if (pullWheelEndTimer) clearTimeout(pullWheelEndTimer)
    pullWheelEndTimer = undefined
  }

  function setPullDistance(next: number, animate: boolean) {
    pullAnimating = animate
    pullPx = Math.min(pullMaxPx, Math.max(0, next))
  }

  function springPullBack() {
    setPullDistance(0, true)
    setTimeout(() => {
      if (pullPx <= 0) pullAnimating = false
    }, 420)
  }

  function commitOrSpringPull() {
    clearPullWheelTimer()
    if (pullNavigating) return
    if (pullPx >= pullThresholdPx && nextNavEntry) {
      pullNavigating = true
      flushProgressUpdate()
      goToReader(readerHref(nextNavEntry))
      return
    }
    springPullBack()
  }

  function growPull(delta: number) {
    if (delta <= 0) return
    pullAnimating = false
    setPullDistance(pullPx + delta * PULL_DAMPING, false)
  }

  function onPullWheel(e: WheelEvent) {
    if (e.deltaY <= 0) {
      if (pullPx > 0) {
        e.preventDefault()
        setPullDistance(pullPx + e.deltaY * PULL_DAMPING, false)
        if (pullPx <= 0) springPullBack()
      }
      return
    }
    if (!atListEnd() && pullPx <= 0) return
    e.preventDefault()
    growPull(e.deltaY)
    clearPullWheelTimer()
    pullWheelEndTimer = setTimeout(() => commitOrSpringPull(), PULL_WHEEL_END_MS)
  }

  function onPullTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) return
    pullTouchStartY = e.touches[0].clientY
    pullTouchStartX = e.touches[0].clientX
    pullTouchLocked = 'none'
    pullTouchActive = atListEnd() || pullPx > 0
  }

  function onPullTouchMove(e: TouchEvent) {
    if (!pullTouchActive || e.touches.length !== 1) return
    const x = e.touches[0].clientX
    const y = e.touches[0].clientY
    const dx = pullTouchStartX - x
    const dy = pullTouchStartY - y

    if (pullTouchLocked === 'none' && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      pullTouchLocked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
    }
    if (pullTouchLocked === 'horizontal') return

    pullTouchStartY = y
    pullTouchStartX = x
    if (dy > 0 && (atListEnd() || pullPx > 0)) {
      e.preventDefault()
      growPull(dy)
    } else if (dy < 0 && pullPx > 0) {
      e.preventDefault()
      setPullDistance(pullPx + dy * PULL_DAMPING, false)
    }
  }

  function onPullTouchEnd() {
    if (!pullTouchActive && pullPx <= 0) return
    pullTouchActive = false
    pullTouchLocked = 'none'
    commitOrSpringPull()
  }

  const pullCueOffset = $derived(Math.min(pullMaxPx, pullPx))
  const pullCueReady = $derived(pullPx >= pullThresholdPx && !!nextNavEntry)
  const pullCueVisible = $derived(pullPx > 0 || pullAnimating)
  /** 0–1 progress toward the release threshold (caps at 1 once past it). */
  const pullProgress = $derived(
    pullThresholdPx > 0 ? Math.min(1, pullPx / pullThresholdPx) : 0,
  )
  const pullRingDashoffset = $derived(PULL_RING_C * (1 - pullProgress))

  /** Non-passive wheel/touch so overscroll can be cancelled at list end. */
  function pullGestures(node: HTMLElement) {
    const nonPassive: AddEventListenerOptions = { passive: false, capture: true }
    const passive: AddEventListenerOptions = { passive: true, capture: true }
    node.addEventListener('wheel', onPullWheel, nonPassive)
    node.addEventListener('touchstart', onPullTouchStart, passive)
    node.addEventListener('touchmove', onPullTouchMove, nonPassive)
    node.addEventListener('touchend', onPullTouchEnd, passive)
    node.addEventListener('touchcancel', onPullTouchEnd, passive)
    return {
      destroy() {
        node.removeEventListener('wheel', onPullWheel, nonPassive)
        node.removeEventListener('touchstart', onPullTouchStart, passive)
        node.removeEventListener('touchmove', onPullTouchMove, nonPassive)
        node.removeEventListener('touchend', onPullTouchEnd, passive)
        node.removeEventListener('touchcancel', onPullTouchEnd, passive)
      },
    }
  }

  $effect(() => {
    const pending = savedPageIndex
    if (pending === null || pageCount === 0 || isResuming) return
    const session = readerSession
    const target = Math.min(Math.max(0, pending), pageCount - 1)
    const offset = savedPageOffset
    untrack(() => {
      savedPageIndex = null
      savedPageOffset = 0
    })
    resumeScroll(target, session, offset)
  })

</script>

<svelte:window bind:innerHeight={null, v => (maxHeight = v!)} onpagehide={flushProgressUpdate} />
<svelte:document onvisibilitychange={onDocumentVisibilityChange} />

<!-- Playback is independent of VList: scrolling does not stop TTS (only stopNovelTts / chapter change). -->
<audio bind:this={audioEl} class="hidden"></audio>

<div class="w-full h-0" bind:clientWidth={null, v => (maxWidth = v!)}></div>

<div bind:clientHeight={null, v => (offsetHeight = v!)} class="#mx-10 #h-8"/>

{#if pages.length == 0}
  <h1 class="mt-20 mx-20">Loading...</h1>
{/if}

<!-- {#if ["WEBTOON", "MANGA"].includes(kind)} -->
<div class="relative" use:pullGestures>
  <VList
    onscroll={() => handleScroll()}
    bind:this={list}
    keepMounted={resumeKeepMounted}
    itemSize={isMangaLike ? mangaPageEstimate : undefined}
    style={`height: ${listHeight}px; filter: brightness(${localReaderOpts.brightness ?? 100}%) contrast(${localReaderOpts.contrast ?? 100}%); overscroll-behavior-y: contain;`}
    bufferSize={listHeight * (isNovelKind ? 5 : 3)}
    data={pages}
    class="overflow-x-hidden overscroll-y-contain scrollbar-none scrollbar-thumb-gray-600 scrollbar-track-gray-900"
    getKey={(page, index) => page ?? `page-${index}`}
  >
    {#snippet children(item, index)}
  {#if isMangaLike}
      <HScrollWithThumb
        class="w-full max-w-full"
        style={`min-height: 350px; padding-inline: ${comicsMarginPx}px;`}
        thumbPosition="top"
        thumbSticky={mangaPageIsDouble[index] === true}
        scrollAlign={mangaPageIsDouble[index] ? 'center' : 'start'}
        thumbClass="mx-2 h-3 -mb-5"
      >
        <MangaPageImage
          {server}
          bookId={bookid}
          pageNum={item}
          baseUrl={BASE_URL}
          baseUrlSuffix={BASE_URL_SUFFIX}
          translateEnabled={comicsTranslateActive}
          retranslateToken={retranslateRequest?.pageNum === item ? retranslateRequest.token : 0}
          onRetranslateDone={onRetranslateConsumed}
          enhanceEnabled={comicsEnhanceActive}
          width={mangaPageDisplayWidth(index)}
          onload={(e) => onMangaPageLoad(e, index)}
          onerror={() => onPageContentResize(index)}
        />
      </HScrollWithThumb>
  {:else if isNovelKind}
      <div
        style="min-height: 400px;"
        class="overflow-x-scroll scrollbar-none px-1"
        use:observePageResize={index}
      >
        {#await renderPage(item)}
        <Spinner class="mx-auto w-5 h-5" />
        {:then p}
        <NovelPageContent
          {server}
          bookId={bookid}
          seriesId={seriesid}
          {type}
          {num}
          subNum={subNum ?? '0'}
          pageNum={item}
          originalTokens={Array.from(p as Iterable<Token>)}
          translateEnabled={novelTranslateActive}
          retranslateToken={retranslateRequest?.pageNum === item ? retranslateRequest.token : 0}
          onRetranslateDone={onRetranslateConsumed}
          tokenDimmed={(ttsIdx) => novelTtsTokenDimmed(index, ttsIdx)}
        />
        {/await}
      </div>
  {/if}
    {/snippet}
  </VList>

  {#if pullCueVisible}
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center pb-16 sm:pb-20"
      style={`transform: translateY(${Math.max(0, 48 - pullCueOffset * 0.55)}px); opacity: ${Math.min(1, pullCueOffset / 40)}; transition: ${pullAnimating ? 'transform 0.4s cubic-bezier(0.22, 1.2, 0.36, 1), opacity 0.35s ease' : 'none'};`}
      aria-hidden="true"
    >
      <div
        class="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow-md backdrop-blur-md {pullCueReady
          ? 'border-latte-mauve/50 bg-latte-base/90 text-latte-mauve dark:border-macchiato-mauve/50 dark:bg-macchiato-base/90 dark:text-macchiato-mauve'
          : 'border-latte-surface1/60 bg-latte-mantle/90 text-latte-text/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/90 dark:text-macchiato-text/70'}"
      >
        <svg
          class="size-6 shrink-0 -rotate-90"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r={PULL_RING_R}
            class="stroke-latte-surface1/50 dark:stroke-macchiato-surface1/50"
            stroke-width="2.5"
            fill="none"
          />
          <circle
            cx="12"
            cy="12"
            r={PULL_RING_R}
            class={pullCueReady
              ? 'stroke-latte-mauve dark:stroke-macchiato-mauve'
              : 'stroke-latte-mauve/70 dark:stroke-macchiato-mauve/70'}
            stroke-width="2.5"
            stroke-linecap="round"
            fill="none"
            stroke-dasharray={PULL_RING_C}
            stroke-dashoffset={pullRingDashoffset}
            style={`transition: ${pullAnimating ? 'stroke-dashoffset 0.35s ease' : 'none'};`}
          />
        </svg>
        {#if nextNavEntry}
          <span>{pullCueReady ? 'Release for' : 'Pull for'} {entryLabel(nextNavEntry)}</span>
          <ChevronRight class="h-3.5 w-3.5 {pullCueReady ? 'opacity-100' : 'opacity-60'}" />
        {:else}
          <span>End of series</span>
        {/if}
      </div>
    </div>
  {/if}
</div>

<div
  style="opacity: {bottomBarVisible ? 1 : 0}; transform: translateY({bottomBarVisible ? 0 : '100%'}); transition: opacity 0.25s ease, transform 0.25s ease;"
  class="fixed inset-x-0 bottom-0 z-40 border-t border-latte-surface1/60 bg-latte-mantle/90 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.35)] backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/90"
  class:pointer-events-none={!bottomBarVisible}
>
  <div class="relative h-14 sm:h-16">
    <!-- Series info — pinned to screen left -->
    <div class="absolute inset-y-0 left-0 z-10 flex items-center">
      <Popover.Root bind:open={tocOpen}>
        <Popover.Trigger
          type="button"
          class="flex h-full min-w-0 max-w-[11rem] items-center gap-2 rounded-none border-y-0 border-l-0 border-r border-latte-surface1/60 bg-latte-base/60 py-1 pl-2 pr-2.5 text-left shadow-sm backdrop-blur-sm transition-colors hover:border-latte-mauve/40 data-[state=open]:border-latte-mauve/50 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/60 dark:hover:border-macchiato-mauve/40 dark:data-[state=open]:border-macchiato-mauve/50 sm:max-w-xs md:max-w-sm sm:pl-3 sm:pr-3"
        >
          <div
            class="{tocCoverClass} bg-cover bg-top"
            style="background-image: url('{coverSrc}')"
            aria-hidden="true"
          ></div>
          <div class="min-w-0 flex-1 py-0.5">
            <span class="text-[10px] font-light tracking-wide text-latte-mauve dark:text-macchiato-mauve">
              {server?.name}
            </span>
            <div class="truncate text-sm font-semibold leading-tight text-latte-text dark:text-macchiato-text">
              {title}
            </div>
            <div class="truncate text-xs text-latte-text/70 dark:text-macchiato-text/70">
              {type === 'volume' ? 'Vol.' : 'Chp.'} {num}{subNum ? '.' + subNum : ''}
            </div>
          </div>
        </Popover.Trigger>

        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          class="w-72 border-latte-surface1/60 bg-latte-mantle/95 p-0 shadow-lg backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95 sm:w-80"
        >
          <button
            type="button"
            class="flex w-full items-center gap-2 border-b border-latte-surface1/60 px-3 py-2.5 text-left text-sm text-latte-text transition-colors hover:bg-latte-surface1/20 hover:text-latte-mauve dark:border-macchiato-surface1/60 dark:text-macchiato-text dark:hover:bg-macchiato-surface1/20 dark:hover:text-macchiato-mauve"
            onclick={() => goToSeries()}
          >
            <ArrowLeft class="h-4 w-4 shrink-0" />
            <span>Return to series page</span>
          </button>

          {#if navEntries.length === 0}
            <div class="flex items-center justify-center py-6">
              <Spinner class="h-4 w-4" />
            </div>
          {:else}
            {@const tocHeight = Math.min(tocListHeightPx, navEntries.length * tocItemSize)}
            <VList
              bind:this={tocList}
              data={navEntries}
              itemSize={tocItemSize}
              style={`height: ${tocHeight}px;`}
              class="overscroll-contain p-1.5 scrollbar-thin scrollbar-thumb-latte-surface1 scrollbar-track-transparent dark:scrollbar-thumb-macchiato-surface1"
              getKey={(book, index) => book?.id ?? `toc-${index}`}
              onscroll={() => ensureVisibleTocPageCounts()}
            >
              {#snippet children(book)}
                {@const current = isCurrentEntry(book)}
                {@const progressPct = entryProgressPercent(book)}
                {@const progressStatus = entryProgressStatus(book)}
                <button
                  type="button"
                  class="mb-0.5 flex w-full items-stretch gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors {current
                    ? 'bg-latte-mauve/15 text-latte-mauve ring-1 ring-latte-mauve/30 dark:bg-macchiato-mauve/15 dark:text-macchiato-mauve dark:ring-macchiato-mauve/30'
                    : 'text-latte-text/90 hover:bg-latte-surface1/20 hover:text-latte-mauve dark:text-macchiato-text/90 dark:hover:bg-macchiato-surface1/20 dark:hover:text-macchiato-mauve'} {book.isCompleted ? 'opacity-90' : ''}"
                  onclick={() => goToReader(readerHref(book))}
                >
                  <div class="{tocCoverClass} self-center">
                    <img
                      src={bookCoverSrc(book.id)}
                      alt=""
                      class="size-full object-cover object-top"
                      onerror={(e) => {
                        const img = e.currentTarget as HTMLImageElement
                        img.src = volChapPlaceholder
                        img.classList.replace('object-cover', 'object-contain')
                      }}
                    />
                  </div>
                  <div class="min-w-0 flex-1 self-center">
                    <div class="mb-1 flex items-center gap-2">
                      <span class="min-w-0 flex-1 truncate font-medium">{entryLabel(book)}</span>
                      {#if book.isCompleted}
                        <Check class="h-3.5 w-3.5 shrink-0 text-latte-green dark:text-macchiato-green" aria-label="Completed" />
                      {/if}
                    </div>
                    <Progress
                      value={progressPct}
                      max={100}
                      class={entryProgressBarClass(book, progressPct)}
                    />
                    <p class="mt-1 truncate text-[10px] tabular-nums text-latte-text/55 dark:text-macchiato-text/55">
                      {progressStatus}
                    </p>
                  </div>
                </button>
              {/snippet}
            </VList>
          {/if}
        </Popover.Content>
      </Popover.Root>
    </div>

    <!-- Page controls — centered on screen -->
    <div class="flex h-full items-center justify-center gap-1.5 px-28 sm:gap-2 sm:px-36">
      {#if isNovelKind}
        <button
          type="button"
          class="flex h-9 w-9 shrink-0 items-center justify-center {readerControl} disabled:opacity-40"
          onclick={() => toggleNovelTtsPlayPause()}
          disabled={pageCount === 0}
          aria-label={novelTtsBusy ? 'Stop read aloud' : novelTtsPage === null ? 'Start read aloud' : novelTtsAudioPlaying ? 'Pause read aloud' : 'Resume read aloud'}
        >
          {#if novelTtsBusy}
            <Spinner class="h-4 w-4" />
          {:else if novelTtsPage !== null && novelTtsAudioPlaying}
            <Pause class="h-4 w-4" />
          {:else}
            <Play class="h-4 w-4" />
          {/if}
        </button>
      {/if}

      <div class="flex h-9 items-stretch overflow-hidden rounded-md border border-latte-surface1/60 bg-latte-base/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/70">
        <!-- <button
          type="button"
          class="flex w-8 items-center justify-center text-latte-text/80 transition-colors hover:bg-latte-surface1/30 hover:text-latte-mauve dark:text-macchiato-text/80 dark:hover:bg-macchiato-surface1/30 dark:hover:text-macchiato-mauve"
          onclick={() => goto(`/media/${serverid}/library/${libraryid}/series/${seriesid}/${type}/${parseInt(num) - 1}`)}
          aria-label="Previous chapter"
        >
          <ChevronLeft class="h-4 w-4" />
        </button> -->

        <Popover.Root bind:open={pageJumpOpen}>
          <Popover.Trigger
            type="button"
            class="flex min-w-[4.5rem] items-center justify-center border-x border-latte-surface1/50 px-2 text-sm font-medium tabular-nums text-latte-text transition-colors hover:bg-latte-surface1/30 hover:text-latte-mauve disabled:opacity-40 dark:border-macchiato-surface1/50 dark:text-macchiato-text dark:hover:bg-macchiato-surface1/30 dark:hover:text-macchiato-mauve sm:min-w-[5.5rem]"
            disabled={pageCount === 0}
            aria-label="Jump to page"
            onclick={() => {
              pageJumpInput = String(currentPage + 1)
            }}
          >
            {#if pageCount === 0}
              <Spinner class="h-4 w-4" />
            {:else}
              {currentPage + 1} / {pageCount}
            {/if}
          </Popover.Trigger>

          <Popover.Content
            side="top"
            align="center"
            sideOffset={8}
            class="w-52 border-latte-surface1/60 bg-latte-mantle/95 p-3 shadow-lg backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
          >
            <form
              class="space-y-2"
              onsubmit={(e) => {
                e.preventDefault()
                jumpToPage()
              }}
            >
              <Label for="page-jump-input" class="text-xs text-latte-text/70 dark:text-macchiato-text/70">
                Go to page
              </Label>
              <div class="flex items-center gap-2">
                <Input
                  id="page-jump-input"
                  type="number"
                  min={1}
                  max={pageCount}
                  bind:value={pageJumpInput}
                  class="h-8 border-latte-surface1/60 bg-latte-base/70 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/70"
                />
                <Button
                  type="submit"
                  size="sm"
                  class="h-8 shrink-0 bg-latte-mauve text-latte-base hover:bg-latte-mauve/90 dark:bg-macchiato-mauve dark:text-macchiato-crust dark:hover:bg-macchiato-mauve/90"
                >
                  Go
                </Button>
              </div>
              <p class="text-[10px] tabular-nums text-latte-text/55 dark:text-macchiato-text/55">
                1 – {pageCount}
              </p>
            </form>
          </Popover.Content>
        </Popover.Root>

        <!-- <button
          type="button"
          class="flex w-8 items-center justify-center text-latte-text/80 transition-colors hover:bg-latte-surface1/30 hover:text-latte-mauve dark:text-macchiato-text/80 dark:hover:bg-macchiato-surface1/30 dark:hover:text-macchiato-mauve"
          onclick={() => goto(`/media/${serverid}/library/${libraryid}/series/${seriesid}/${type}/${parseInt(num) + 1}`)}
          aria-label="Next chapter"
        >
          <ChevronRight class="h-4 w-4" />
        </button> -->
      </div>
    </div>

    <!-- Reader settings — pinned to screen right -->
    <div class="absolute inset-y-0 right-0 z-10 flex items-center pr-2 sm:pr-3">
      <Drawer.Root>
        <Drawer.Trigger
          type="button"
          class="flex h-9 w-9 items-center justify-center {readerControl}"
          aria-label="Reader settings"
        >
          <SlidersHorizontal class="h-4 w-4" />
        </Drawer.Trigger>

        <Drawer.Content
          class="border-latte-surface1/60 bg-latte-mantle/95 backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
        >
          <div class="mx-auto flex max-h-[inherit] w-full max-w-lg flex-col px-4 pt-2">
            <Drawer.Header class="shrink-0 space-y-1 px-0 pb-4 pt-0 text-left">
              <span class={sectionLabel}>reader</span>
              <Drawer.Title class="text-lg font-semibold text-latte-text dark:text-macchiato-text">
                Settings
              </Drawer.Title>
              <Drawer.Description class="text-sm text-latte-text/70 dark:text-macchiato-text/70">
                Adjust layout, typography, and display for this session.
              </Drawer.Description>
            </Drawer.Header>

            <div class="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain pb-2">
              <section class="space-y-3 rounded-md border border-latte-surface1/60 bg-latte-base/35 p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/35">
                <h3 class={sectionLabel}>layout</h3>

                <div class="space-y-2">
                  <div class="flex items-center justify-between gap-3">
                    <Label class="text-sm text-latte-text dark:text-macchiato-text">Side margin</Label>
                    <span class="text-xs tabular-nums text-latte-text/60 dark:text-macchiato-text/60">
                      {marginPct}%
                    </span>
                  </div>
                  <Slider
                    type="single"
                    bind:value={localReaderOpts.margin[isMangaLike ? 'comics' : 'novel']}
                    max={50}
                    step={1}
                    class="w-full"
                  />
                  <p class="text-xs text-latte-text/55 dark:text-macchiato-text/55">
                    Inset each side as a percent of screen width (max 50%).
                  </p>
                </div>

                <div class="space-y-2">
                  <div class="flex items-center justify-between gap-3">
                    <Label class="text-sm text-latte-text dark:text-macchiato-text">Brightness</Label>
                    <span class="text-xs tabular-nums text-latte-text/60 dark:text-macchiato-text/60">{localReaderOpts.brightness}%</span>
                  </div>
                  <Slider
                    type="single"
                    bind:value={localReaderOpts.brightness}
                    max={100}
                    min={25}
                    step={5}
                    class="w-full"
                  />
                </div>
              </section>

              {#if isMangaLike}
                <section class="space-y-3 rounded-md border border-latte-surface1/60 bg-latte-base/35 p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/35">
                  <h3 class={sectionLabel}>translation</h3>

                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <Label for="comics-translate" class="text-sm text-latte-text dark:text-macchiato-text">
                        Translate to English
                      </Label>
                      {#if !mangaTranslationAvailable}
                        <p class="text-xs text-latte-peach dark:text-macchiato-peach">
                          disabled server-side
                        </p>
                      {/if}
                    </div>
                    <Switch
                      id="comics-translate"
                      bind:checked={localReaderOpts.comicsTranslate}
                      disabled={!mangaTranslationAvailable}
                    />
                  </div>

                  <div class="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      class="w-full border-latte-surface1/60"
                      disabled={!comicsTranslateActive || retranslateRequest !== null}
                      onclick={requestRetranslateCurrentPage}
                    >
                      {retranslateRequest !== null ? 'Retranslating…' : 'Retranslate this page'}
                    </Button>
                    <p class="text-xs text-latte-text/55 dark:text-macchiato-text/55">
                      Force a fresh translation for the current page only.
                    </p>
                  </div>
                </section>

                <section class="space-y-3 rounded-md border border-latte-surface1/60 bg-latte-base/35 p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/35">
                  <h3 class={sectionLabel}>image quality</h3>

                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <Label for="comics-enhance" class="text-sm text-latte-text dark:text-macchiato-text">
                        Upscale low-res pages
                      </Label>
                      <p class="text-xs {mangaEnhanceAvailable ? 'text-latte-text/55 dark:text-macchiato-text/55' : 'text-latte-peach dark:text-macchiato-peach'}">
                        {#if !mangaEnhanceAvailable}
                          disabled server-side
                        {:else if comicsTranslateActive}
                          Paused while translating
                        {:else}
                          Show original first, then swap when enhanced
                        {/if}
                      </p>
                    </div>
                    <Switch
                      id="comics-enhance"
                      bind:checked={localReaderOpts.comicsEnhance}
                      disabled={!mangaEnhanceAvailable || comicsTranslateActive}
                    />
                  </div>
                </section>
              {:else if isNovelKind}
                <section class="space-y-3 rounded-md border border-latte-surface1/60 bg-latte-base/35 p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/35">
                  <h3 class={sectionLabel}>translation</h3>

                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <Label for="novel-translate" class="text-sm text-latte-text dark:text-macchiato-text">
                        Translate to English
                      </Label>
                      {#if !novelTranslationAvailable}
                        <p class="text-xs text-latte-peach dark:text-macchiato-peach">
                          disabled server-side
                        </p>
                      {/if}
                    </div>
                    <Switch
                      id="novel-translate"
                      checked={localReaderOpts.novelTranslate}
                      onCheckedChange={(v: boolean) => {
                        localReaderOpts.novelTranslate = v
                        if (!v) clearNovelTranslationCache()
                        retranslateRequest = null
                      }}
                      disabled={!novelTranslationAvailable}
                    />
                  </div>

                  <div class="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      class="w-full border-latte-surface1/60"
                      disabled={!novelTranslateActive || retranslateRequest !== null}
                      onclick={requestRetranslateCurrentPage}
                    >
                      {retranslateRequest !== null ? 'Retranslating…' : 'Retranslate this page'}
                    </Button>
                    <p class="text-xs text-latte-text/55 dark:text-macchiato-text/55">
                      Force a fresh translation for the current page only.
                    </p>
                  </div>
                </section>
              {/if}

              <section class="space-y-3 rounded-md border border-latte-surface1/60 bg-latte-base/35 p-3 dark:border-macchiato-surface1/60 dark:bg-macchiato-base/35">
                <h3 class={sectionLabel}>typography</h3>

                <div class="space-y-2">
                  <div class="flex items-center justify-between gap-3">
                    <Label class="text-sm text-latte-text dark:text-macchiato-text">Font size</Label>
                    <span class="text-xs tabular-nums text-latte-text/60 dark:text-macchiato-text/60">{localReaderOpts.fontSize}px</span>
                  </div>
                  <Slider
                    type="single"
                    bind:value={localReaderOpts.fontSize}
                    max={72}
                    min={12}
                    step={1}
                    class="w-full"
                  />
                </div>

                <div class="space-y-2">
                  <div class="flex items-center justify-between gap-3">
                    <Label class="text-sm text-latte-text dark:text-macchiato-text">Line spacing</Label>
                    <span class="text-xs tabular-nums text-latte-text/60 dark:text-macchiato-text/60">{localReaderOpts.verticalSpacing}px</span>
                  </div>
                  <Slider
                    type="single"
                    bind:value={localReaderOpts.verticalSpacing}
                    max={40}
                    min={8}
                    step={1}
                    class="w-full"
                  />
                </div>

                <div class="flex items-center justify-between gap-3">
                  <Label for="justify" class="text-sm text-latte-text dark:text-macchiato-text">Justify text</Label>
                  <Switch id="justify" bind:checked={localReaderOpts.justify} />
                </div>

                <div class="space-y-2">
                  <Label class="text-sm text-latte-text dark:text-macchiato-text">Body font</Label>
                  <div class="flex flex-wrap gap-1.5">
                    {#each fontChoices as font (font.id)}
                      <button
                        type="button"
                        class="rounded-md px-2.5 py-1 text-xs transition-colors {localReaderOpts.bodyFont === font.id
                          ? 'bg-latte-mauve/20 text-latte-mauve ring-1 ring-latte-mauve/40 dark:bg-macchiato-mauve/20 dark:text-macchiato-mauve dark:ring-macchiato-mauve/40'
                          : 'border border-latte-surface1/60 text-latte-text/80 hover:border-latte-mauve/40 hover:text-latte-mauve dark:border-macchiato-surface1/60 dark:text-macchiato-text/80 dark:hover:border-macchiato-mauve/40 dark:hover:text-macchiato-mauve'}"
                        onclick={() => (localReaderOpts.bodyFont = font.id)}
                      >
                        {font.label}
                      </button>
                    {/each}
                  </div>
                </div>
              </section>
            </div>

            <Drawer.Footer class="shrink-0 px-0 pb-6 pt-5">
              <Drawer.Close
                class="{buttonVariants({ variant: 'outline' })} w-full border-latte-surface1/60 bg-transparent text-latte-text hover:border-latte-mauve/40 hover:bg-latte-surface1/25 hover:text-latte-mauve dark:border-macchiato-surface1/60 dark:text-macchiato-text dark:hover:border-macchiato-mauve/40 dark:hover:bg-macchiato-surface1/25 dark:hover:text-macchiato-mauve"
              >
                <X class="mr-1.5 h-4 w-4" />
                Close
              </Drawer.Close>
            </Drawer.Footer>
          </div>
        </Drawer.Content>
      </Drawer.Root>
    </div>
  </div>
</div>


<style>
  .reader-page-img {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
</style>
